import { Box, Typography } from "@mui/material";
import { PieChart } from "@mui/x-charts/PieChart";
import { useState } from "react";
import CustomLegendItem from "../../../../dashboard/mssp_widgets/assets_breakdown/CustomLegendItem";
import TrendIndicator from "../../TrendIndicator";

const DonutPieChartPercentage = ({
  data = [],
  type = "entities",
  label = "Entities",
  showPercent = false,
  centerDisplay = "average",
  equalSlices = true,
  showLegend = true,
  showTrend = true,
  CustomTooltipComponent = null,
}) => {
  const [highlightedItem, setHighlightedItem] = useState(null);

  const totalValue = data.reduce((acc, item) => acc + item.value, 0);

  const averageValue =
    data.length > 0 ? Math.round(totalValue / data.length) : 0;

  const totalDiff =
    data.reduce(
      (acc, item) =>
        acc +
        (showPercent
          ? item?.trend?.[type]?.percentChange
          : item?.trend?.[type]?.diff) *
          (item?.trend?.[type]?.isPositive === true ? 1 : -1),
      0
    ) || 0;

  const averageTrend = {
    diff: totalDiff < 0 ? totalDiff * -1 : totalDiff,
    isPositive: totalDiff > 0,
    percentChange: totalDiff < 0 ? totalDiff * -1 : totalDiff,
  };

  const chartData = data.map((item) => {
    const sliceValue = equalSlices
      ? (100 / data.length).toFixed(1)
      : item.value;

    return {
      id: item.id,
      value: sliceValue,
      originalValue: item.value,
      originalPercentage: showPercent
        ? item.value
        : ((item.value / totalValue) * 100).toFixed(1),
      label: item.label,
      color: item.color,
      trend: item?.trend?.[type] || 0,
      actualPercentage: showPercent
        ? item.value
        : ((item.value / totalValue) * 100).toFixed(1),
      othersNames: item.othersNames,
      tooltip:
        item.othersNames && item.othersNames.length > 0
          ? CustomTooltipComponent
          : null,
    };
  });

  const getHighlightedChartItem = () => {
    if (!highlightedItem) return null;

    const dataIndex = highlightedItem.dataIndex;
    if (typeof dataIndex === "number") {
      return chartData[dataIndex];
    }
    return chartData.find((i) => i.id === dataIndex);
  };

  const getCenterValue = () => {
    const item = getHighlightedChartItem();

    if (item) {
      if (showPercent) {
        return `${item.originalValue || 0}%`;
      }
      return equalSlices
        ? `${item.actualPercentage || 0}%`
        : item.originalValue || 0;
    }

    if (centerDisplay === "total") {
      return totalValue;
    }

    return showPercent ? `${averageValue}%` : averageValue;
  };

  const getCenterLabel = () => {
    const item = getHighlightedChartItem();
    if (item) {
      return item.label;
    }
    return centerDisplay === "total" ? `Total ${label}` : `Average ${label}`;
  };

  return (
    <Box
      sx={{
        position: "relative",
        height: 208,
        display: "flex",
        alignItems: "center",
        gap: showLegend ? "40px" : 0,
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: showLegend ? "calc(100% - 150px)" : "100%",
          height: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <PieChart
          series={[
            {
              data: chartData,
              innerRadius: 75,
              outerRadius: 90,
              paddingAngle: 0.5,
              startAngle: -90,
              endAngle: 270,
              cornerRadius: 2,
              highlightScope: {
                faded: "global",
                highlighted: "item",
              },
              id: "pie-segment",
              valueFormatter: (item) => {
                const chartItem = chartData.find((d) => d.id === item.id);
                if (!chartItem) return `${item.value}`;
                return `${chartItem.originalValue}${showPercent ? "%" : ""}`;
              },
            },
          ]}
          slotProps={{
            legend: {
              hidden: true,
            },
            pieArcLabel: {
              style: {
                fontSize: "10px",
                fill: "white",
                fontWeight: "bold",
              },
            },
            tooltip: {
              trigger: "item",
            },
          }}
          height={208}
          width={208}
          margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
          highlightedItem={highlightedItem}
          onHighlightChange={setHighlightedItem}
        />
        <Box
          sx={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            textAlign: "center",
            width: "100%",
            maxWidth: 120,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          <Typography variant="h5">{getCenterValue()}</Typography>
          <Typography variant="body2">{getCenterLabel()}</Typography>
          {showTrend &&
            (getHighlightedChartItem()?.trend ? (
              <TrendIndicator
                data={getHighlightedChartItem().trend}
                showPercent={showPercent}
              />
            ) : !highlightedItem && averageTrend ? (
              <TrendIndicator data={averageTrend} showPercent={showPercent} />
            ) : null)}
        </Box>
      </Box>
      {showLegend && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: 2,
          }}
        >
          {chartData.map((item) => (
            <CustomLegendItem
              key={item.id}
              item={item}
              highlightedItem={highlightedItem}
              onHover={setHighlightedItem}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default DonutPieChartPercentage;
